import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCart } from "../../../contexts/CartContext";
import { InformationCircleIcon } from "@heroicons/react/24/solid";

const AddToCartShop = ({ product, onClose }) => {
  const { t, i18n } = useTranslation();
  const { cart } = useCart();
  const lang = i18n.language.split("-")[0];

  const [fullProduct, setFullProduct] = useState(product);
  const [loadingProduct, setLoadingProduct] = useState(true);

  const [priceTypes, setPriceTypes] = useState([]);
  const [loadingPriceTypes, setLoadingPriceTypes] = useState(true);

  const [selectedOptions, setSelectedOptions] = useState({});
  const [globalQuantity, setGlobalQuantity] = useState(1);

  //
  // 🚀 1. Charger les détails du produit
  //
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/store/product-details/${product.id}`
        );
        if (res.ok) {
          setFullProduct(await res.json());
        }
      } catch (e) {
        console.error("Erreur product-details:", e);
      }
      setLoadingProduct(false);
    };
    load();
  }, [product.id]);

  //
  // 🚀 2. Charger les PriceTypes
  //
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/pricetype`
        );
        if (res.ok) {
          setPriceTypes(await res.json());
        }
      } catch (e) {
        console.error("Erreur price-type:", e);
      }
      setLoadingPriceTypes(false);
    };
    load();
  }, []);

  //
  // 🚀 3. Déterminer le priceType du produit
  //
  const priceType = priceTypes.find((pt) => pt.id === fullProduct.priceTypeId);

  //
  // 🚀 4. Helpers – Options
  //
  const getSelectedConfiguration = () => {
    const cfg = fullProduct.productConfigurations?.[0];
    const idx = selectedOptions[cfg?.id];
    return cfg?.values?.[idx];
  };

  const getQuantityInCart = () => {
    const selected = getSelectedConfiguration();
    if (!selected) return 0;

    const match = cart.find(
      (c) => c.id === fullProduct.id && c.selectedOptionId === selected.id
    );

    return match?.quantity || 0;
  };

  //
  // 🚀 5. Calcul du stock disponible
  //
  const getAvailableQuantity = () => {
    const selected = getSelectedConfiguration();
    const inCart = getQuantityInCart();
    const limits = fullProduct.productInventoryLimits;

    // Option avec quantité spécifique
    if (selected?.quantity != null) {
      return Math.max(0, selected.quantity - inCart);
    }

    // Inventaire fixe / quotidien
    if (
      limits?.inventoryMode === "fixed" ||
      limits?.inventoryMode === "daily"
    ) {
      return Math.max(0, (limits?.totalQuantity ?? 0) - inCart);
    }

    // Illimité: si tu veux totalement retirer ce mode :
    return Math.max(0, (limits?.totalQuantity ?? 0) - inCart);
  };

  const availableQty = getAvailableQuantity();
  const isOutOfStock = availableQty <= 0;

  //
  // 🚀 6. Sélection automatique de la première option disponible
  //
  useEffect(() => {
    if (
      fullProduct.productConfigurations?.length > 0 &&
      Object.keys(selectedOptions).length === 0
    ) {
      const config = fullProduct.productConfigurations[0];
      const firstIndex = config.values.findIndex((v) => v.quantity > 0);
      if (firstIndex !== -1) {
        setSelectedOptions({ [config.id]: firstIndex });
      }
    }
  }, [fullProduct]);

  //
  // 🚀 7. Prix total
  //
  const calculateTotalPrice = () => {
    const base = fullProduct.price || 0;

    const surplus =
      fullProduct.productConfigurations?.reduce((sum, cfg) => {
        const idx = selectedOptions[cfg.id];
        const val = cfg.values[idx];
        return sum + (val?.price || 0);
      }, 0) || 0;

    return ((base + surplus) * globalQuantity).toFixed(2);
  };

  //
  // 🚀 8. Logic "Amazon style" pour Quantité
  //
  const renderQuantityField = () => {
    if (availableQty <= 10) {
      // Dropdown simple
      return (
        <select
          value={globalQuantity}
          onChange={(e) => setGlobalQuantity(Number(e.target.value))}
          className="border px-3 py-1 rounded"
        >
          {Array.from({ length: availableQty }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1}
            </option>
          ))}
        </select>
      );
    }

    // Stock important → input number
    return (
      <input
        type="number"
        min={1}
        max={availableQty}
        value={globalQuantity}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (v >= 1 && v <= availableQty) setGlobalQuantity(v);
        }}
        className="border px-3 py-1 rounded w-24"
      />
    );
  };

  //
  // 🚀 9. Ajouter
  //
  const handleAdd = () => {
    const selected = getSelectedConfiguration();

    onClose({
      ...fullProduct,
      quantity: globalQuantity,
      version: fullProduct.productInventoryLimits?.version ?? 0,
      selectedOptionId: selected?.id || null,
      OptionName: selected
        ? lang === "fr"
          ? selected.nameFr
          : selected.nameEn
        : null,
      OptionSurplus: selected?.price || 0,
      totalPrice: parseFloat(calculateTotalPrice()),
    });
  };

  //
  // 🚀 10. Render complet
  //
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 text-primary">
      <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6 relative">
        <button
          onClick={() => onClose(null)}
          className="absolute top-4 right-4 text-gray-500 hover:text-black"
        >
          ✕
        </button>

        <h2 className="text-xl font-bold mb-2">
          {lang === "fr" ? fullProduct.nameFr : fullProduct.nameEn}
        </h2>

        {fullProduct?.hasShipping === false && (
          <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg mb-2">
            <InformationCircleIcon className="w-5 h-5" />
            <span>
              {t("store.no_shipping_for_item", "Pas de livraison disponible")}
            </span>
          </div>
        )}

        <p className="text-gray-600 text-sm mb-4">
          {lang === "fr"
            ? fullProduct.descriptionFr
            : fullProduct.descriptionEn}
        </p>

        <div className="mb-4">
          <p className="font-semibold">{t("cart.basePrice")}</p>
          <p>${fullProduct.price.toFixed(2)}</p>
        </div>

        {fullProduct.productConfigurations?.map((cfg) => {
          const selectedIndex = selectedOptions[cfg.id];
          return (
            <div key={cfg.id} className="mb-4">
              <p className="font-semibold capitalize mb-2">{cfg.type}</p>

              <div className="flex flex-wrap gap-2">
                {cfg.values.map((val, i) => {
                  const isSelected = selectedIndex === i;
                  const disabled = val.quantity === 0;

                  return (
                    <div
                      key={val.id}
                      onClick={() =>
                        !disabled && setSelectedOptions({ [cfg.id]: i })
                      }
                      className={`px-3 py-1 rounded-full border text-sm
                      ${
                        disabled
                          ? "cursor-not-allowed opacity-40"
                          : "cursor-pointer"
                      }
                      ${
                        isSelected
                          ? "bg-primary text-white border-primary"
                          : "border-gray-300"
                      }`}
                    >
                      {lang === "fr" ? val.nameFr : val.nameEn}
                      {val.price > 0 && (
                        <span className="text-xs ml-1">
                          (+{val.price.toFixed(2)}$)
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {cfg.values.every((v) => v.quantity === 0) && (
                <p className="text-sm text-red-600 font-semibold mt-1">
                  {t("cart.outOfStock")}
                </p>
              )}
            </div>
          );
        })}

        {/* Quantité */}
        {!isOutOfStock && (
          <div className="mb-4">
            <label className="block font-semibold mb-1">
              {t("cart.quantity")}
            </label>
            {renderQuantityField()}
            <p className="text-xs text-gray-500 mt-1">
              {t("cart.max")} : {availableQty}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 text-right flex justify-between items-center">
          <p className="text-lg font-semibold">
            Total:{" "}
            <span className="text-primary">${calculateTotalPrice()}</span>
          </p>

          {isOutOfStock ? (
            <button
              onClick={() => onClose(null)}
              className="px-6 py-2 rounded-full bg-gray-500 text-white hover:bg-gray-600"
            >
              {t("global.close", "Fermer")}
            </button>
          ) : (
            <button
              onClick={handleAdd}
              className="px-6 py-2 rounded-full bg-primary text-white hover:bg-primary/90"
            >
              {t("global.add_to_cart", "Ajouter")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddToCartShop;
